import { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { DataHubSpot } from 'app/[lang]/(main)/integrations/hubspot/_components/utils/getDataHubSpotTree';
import { dialects } from 'constants/dialects';
import {
  HubspotIntegrationTypeEnum,
  HubspotPageTypeEnum,
  InputMaybe,
  IntegrationEnum,
  MetadataContextEnum,
  MetadataSourceEnum,
  UploadedFileMetadataInput
} from 'gql/graphql';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAllOrderableTargetLanguagesCodes } from 'providers/ClientProviders/useAllOrderableTargetLanguages';
import { useClientAccountUpdateDialects } from 'providers/ClientProviders/useClientAccountUpdateDialects';
import { useHubSpotIntegration } from 'providers/HubSpotIntegrationProviders/useHubSpotIntegration';
import { useHubSpotIntegrationGetEmailMutation } from 'providers/HubSpotIntegrationProviders/useHubSpotIntegrationGetEmailMutation';
import { useHubSpotIntegrationGetFormMutation } from 'providers/HubSpotIntegrationProviders/useHubSpotIntegrationGetFormMutation';
import { useHubSpotIntegrationGetPageMutation } from 'providers/HubSpotIntegrationProviders/useHubSpotIntegrationGetPageMutation';
import { KeysEnum } from 'types/KeysEnum';
import { useDebouncedCallback } from 'use-debounce';
import { findClosestLanguage } from 'utils/findClosestLanguage';
import { jsonToFile } from 'utils/jsonToFile';
import { extractParam } from 'utils/paramsUtil';
import { parseLanguageToTolqFormat } from 'utils/parseLanguageCode';
import {
  setFileToUpload,
  setFilteredContent,
  setMetadata,
  updateInputField,
  useUploadingContentFlow
} from '../_actions';
import { getAllValidValues } from '../_utilsTyped';

interface IHubSpotIntegration {
  setFileToUpload: (file: File) => void;
  setFilteredContent: (filteredContent: string[]) => void;
  setMetadata: (metadata: any) => void;
  useUploadingContentFlow: () => void;
  updateInputField: (data: { field: string; value: any }) => void;
  allowedTypes: string[];
  supportedFormats: string[];
}

const HubSpotIntegration: FC<IHubSpotIntegration> = ({
  setMetadata,
  setFilteredContent,
  setFileToUpload,
  useUploadingContentFlow: uploadingContentFlow,
  updateInputField
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { data } = useHubSpotIntegration({ type: HubspotIntegrationTypeEnum.Hubdb });
  const { data: allOrderableTargetLanguagesCodes } = useAllOrderableTargetLanguagesCodes();

  const { mutateAsync: hubspotIntegrationGetPageMutation } = useHubSpotIntegrationGetPageMutation();
  const { mutateAsync: hubspotIntegrationGetEmailMutation } =
    useHubSpotIntegrationGetEmailMutation();
  const { mutateAsync: hubspotIntegrationGetFormMutation } = useHubSpotIntegrationGetFormMutation();

  const { mutateAsync: clientAccountUpdateDialects } = useClientAccountUpdateDialects();

  const [loading, setLoading] = useState(false);

  const ids = extractParam(searchParams, KeysEnum.Ids, []) as string[];
  const sourceLanguage = extractParam(searchParams, KeysEnum.SourceLanguage, '') as string;
  const keys = extractParam(searchParams, KeysEnum.Keys, '') as string[];
  const type = extractParam(searchParams, KeysEnum.Type, '') as MetadataContextEnum;
  const addon = pathname.split('/translation/new/')?.at(1);

  const genericMutation = useCallback(
    async (id: string) => {
      if (type === MetadataContextEnum.Emails)
        return hubspotIntegrationGetEmailMutation({
          emailId: id
        });

      if (type === MetadataContextEnum.Forms)
        return hubspotIntegrationGetFormMutation({
          formId: id
        });

      return hubspotIntegrationGetPageMutation({
        pageId: id,
        pageType: type as unknown as InputMaybe<HubspotPageTypeEnum>
      });
    },
    [
      hubspotIntegrationGetEmailMutation,
      hubspotIntegrationGetFormMutation,
      hubspotIntegrationGetPageMutation,
      type
    ]
  );

  const genericMetadata = useCallback(
    (page: DataHubSpot) => {
      const metadata: UploadedFileMetadataInput = {
        filename: page.name + '.json',
        source: MetadataSourceEnum.Hubspot,
        context: type
      };

      if (![MetadataContextEnum.Emails, MetadataContextEnum.Forms].includes(type))
        metadata.hubspotSiteId = page.id;

      return metadata;
    },
    [type]
  );

  const getDialects = (languagesTolq?: (string | undefined)[]) =>
    languagesTolq?.reduce((acc, locale) => {
      const result = locale?.split('_');
      if (result?.at(1)) {
        const [lang, region] = result;
        if (!acc[lang]) {
          acc[lang] = { allowed: [], default: false };
        }
        acc[lang].allowed.push(region);
      }
      return acc;
    }, {} as Record<string, { allowed: string[]; default: boolean }>);

  const getLanguagesTolq = useCallback(
    (languages: Set<string>) =>
      Array.from(languages)
        .map((lang) =>
          findClosestLanguage(parseLanguageToTolqFormat(lang) ?? '', [
            ...(allOrderableTargetLanguagesCodes ?? []),
            ...(dialects ?? [])
          ])
        )
        .filter(Boolean),
    [allOrderableTargetLanguagesCodes]
  );

  const fetchFiles = useCallback(async () => {
    if (type === MetadataContextEnum.Hubdb) return;
    if (
      data?.hubspotIntegration?.id &&
      addon === IntegrationEnum.Hubspot &&
      type &&
      ids?.every(String) &&
      !!allOrderableTargetLanguagesCodes?.length &&
      !loading
    ) {
      setLoading(true);

      updateInputField({ field: 'sourceLanguageCode', value: sourceLanguage });

      const jsonContents = await Promise.all(ids.map(genericMutation));

      let filteredContent = [] as string[];
      const languages = jsonContents.reduce((accLanguages, page) => {
        filteredContent = [...filteredContent, ...getAllValidValues(page, keys)];

        setFileToUpload(jsonToFile(page, page.name));
        setMetadata(genericMetadata(page));

        if (!('translations' in page) || !page?.translations) return accLanguages;

        return Object.keys(page.translations).reduce((acc, keyLanguage: string) => {
          if (keyLanguage && 'language' in page && keyLanguage !== page.language)
            acc.add(keyLanguage);

          return acc;
        }, accLanguages);
      }, new Set<string>());

      setFilteredContent(filteredContent);

      const languagesTolq = getLanguagesTolq(languages);
      const hasDialects = languagesTolq.some((lang) => lang?.includes('_'));
      if (hasDialects) await clientAccountUpdateDialects({ dialects: getDialects(languagesTolq) });

      updateInputField({ field: 'targetLanguageCodes', value: languagesTolq as string[] });
      updateInputField({ field: 'integration', value: IntegrationEnum.Hubspot });

      if (keys.length > 0) updateInputField({ field: 'included', value: keys });

      uploadingContentFlow();
    }
  }, [
    type,
    data?.hubspotIntegration?.id,
    addon,
    ids,
    allOrderableTargetLanguagesCodes?.length,
    loading,
    updateInputField,
    sourceLanguage,
    genericMutation,
    setFilteredContent,
    getLanguagesTolq,
    clientAccountUpdateDialects,
    keys,
    uploadingContentFlow,
    setFileToUpload,
    setMetadata,
    genericMetadata
  ]);

  const debouncedMutation = useDebouncedCallback(fetchFiles, 1);

  useEffect(() => {
    debouncedMutation();
  }, [
    loading,
    addon,
    ids,
    sourceLanguage,
    keys,
    type,
    allOrderableTargetLanguagesCodes,
    fetchFiles,
    debouncedMutation
  ]);

  return null;
};

export default connect(
  ({ staticData: { supportedFormats, allowedTypes } }) => ({ supportedFormats, allowedTypes }),
  {
    setFileToUpload,
    setMetadata,
    setFilteredContent,
    useUploadingContentFlow,
    updateInputField
  }
)(HubSpotIntegration);
